import { api, apiService } from '@/services/core/ApiService'
import { whileSameSession } from '@/services/core/sessionGuard'
import { useAuthStore } from '@/stores/useAuthStore'
import {
    ChangePasswordRequestSchema,
    CustomerResponseSchema,
    type IChangePasswordRequest,
    type ICustomer,
    type IUpdateCompanyCustomerRequest,
    type IUpdateCustomerRequest,
    UpdateCompanyCustomerRequestSchema,
    UpdateCustomerRequestSchema,
} from '~/schemas/customer/customer'

// ===== EDITAR PF =====

/**
 * `PUT customer/update` (pessoa física). O customer devolvido vai para a store. Sem o toast padrão: os formulários
 * (perfil e EditUserModal) usam o `useFormAction`, que mostra o erro no campo ou num aviso, uma vez só.
 */
export const updateCustomerService = (data: IUpdateCustomerRequest): Promise<ICustomer> =>
    apiService(api)
        .put('customer/update', () => UpdateCustomerRequestSchema.parse(data), CustomerResponseSchema, {
            meta: { showLoader: true },
        })
        .onSuccess(whileSameSession((response) => useAuthStore.getState().setCustomer(response.customer)))
        .onError(async () => {}, { useDefaultErrorHandler: false })
        .execute()
        .then((response) => response.customer)

// ===== EDITAR PJ =====

/** `PUT customer/update-company-customer` (pessoa jurídica). O customer devolvido vai para a store. Sem o toast padrão (`useFormAction`). */
export const updateCompanyCustomerService = (data: IUpdateCompanyCustomerRequest): Promise<ICustomer> =>
    apiService(api)
        .put(
            'customer/update-company-customer',
            () => UpdateCompanyCustomerRequestSchema.parse(data),
            CustomerResponseSchema,
            {
                meta: { showLoader: true },
            },
        )
        .onSuccess(whileSameSession((response) => useAuthStore.getState().setCustomer(response.customer)))
        .onError(async () => {}, { useDefaultErrorHandler: false })
        .execute()
        .then((response) => response.customer)

// ===== TROCAR SENHA =====

/**
 * `PUT customer/change-password` (tela /perfil/alterar-senha). O e-mail vai o do cliente logado (store). A resposta traz
 * o customer de antes da troca, sem nada novo para a store. Sem o toast padrão: o formulário usa o `useFormAction`
 * (regra da senha nova por campo, senha atual errada como mensagem), que mostra o erro uma vez só.
 */
export const changePasswordService = (data: Omit<IChangePasswordRequest, 'email'>): Promise<void> => {
    const email = useAuthStore.getState().data?.customer?.email ?? ''
    return apiService(api)
        .put(
            'customer/change-password',
            () => ChangePasswordRequestSchema.parse({ ...data, email }),
            CustomerResponseSchema,
            {
                meta: { showLoader: true },
            },
        )
        .onError(async () => {}, { useDefaultErrorHandler: false })
        .execute()
        .then(() => undefined)
}
